import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AssetDto, CommentDto, ForeignRef } from '@picflow/types';
import { lastValueFrom } from 'rxjs';
import { commentsPerPage } from '../consts';
import { AnnotationColorToHex, AnnotationHexToColor } from '../gallery-shared/types/annotation-color';
import { Shape } from '../gallery-shared/types/shapes';
import { copy } from '../lib/copy';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { GalleryPasswordService } from './gallery-password.service';

@Injectable({
  providedIn: 'root',
})
export class CommentService {
  constructor(
    private http: HttpClient,
    private config: AppConfigService,
    private loader: DataLoaderService,
    private galleryPasswordService: GalleryPasswordService
  ) {}

  public async getComments(
    assetId: ForeignRef<AssetDto>,
    galleryId: string,
    page: number = 0,
    _comments: CommentDto[] = []
  ): Promise<CommentDto[]> {
    const url = `${
      this.config.apiEndpoint
    }/assets/${assetId}/comments?limit=${commentsPerPage}&page=${page}&sort[]=-created_at${this.getPassword(
      galleryId,
      true
    )}`;
    const resp = <any>await lastValueFrom(this.http.get(url));
    resp.data.forEach(this.fixAnnotation.bind(this));
    const comments = [..._comments, ...resp.data.filter(comment => comment.asset === assetId)];
    const isMore = resp.data.length === commentsPerPage;
    if (isMore) return this.getComments(assetId, galleryId, page + 1, comments);
    return comments;
  }

  public async getFirstComment(assetId: ForeignRef<AssetDto>, galleryId: string): Promise<CommentDto> {
    return this.loader.get(`first_comment_${assetId}${galleryId}`, async () => {
      const url = `${this.config.apiEndpoint}/assets/${assetId}/comments?limit=${1}&page=${0}${this.getPassword(
        galleryId,
        true
      )}`;
      const resp = <any>await lastValueFrom(this.http.get(url));
      return resp.data[0];
    });
  }

  public async getComment(commentId: string, assetId: string, galleryId: string): Promise<CommentDto> {
    const url = `${this.config.apiEndpoint}/assets/${assetId}/comments/${commentId}${this.getPassword(galleryId)}`;
    const resp = <any>await lastValueFrom(this.http.get(url));
    this.fixAnnotation(resp);
    return resp;
  }

  public async postComment(assetId: ForeignRef<AssetDto>, text: string, galleryId: string): Promise<CommentDto> {
    return <any>(
      await lastValueFrom(
        this.http.post(`${this.config.apiEndpoint}/assets/${assetId}/comments${this.getPassword(galleryId)}`, { text })
      )
    );
  }

  public async updateComment(
    assetId: ForeignRef<AssetDto>,
    commentId: string,
    params: any,
    galleryId: string
  ): Promise<CommentDto> {
    return <any>(
      await lastValueFrom(
        this.http.patch(
          `${this.config.apiEndpoint}/assets/${assetId}/comments/${commentId}${this.getPassword(galleryId)}`,
          params
        )
      )
    );
  }

  public async deleteComment(assetId: string, commentId: string, galleryId: string) {
    return <any>(
      await lastValueFrom(
        this.http.delete(
          `${this.config.apiEndpoint}/assets/${assetId}/comments/${commentId}${this.getPassword(galleryId)}`
        )
      )
    );
  }

  public async postAnnotation(asset: AssetDto, data: Shape[], galleryId: string): Promise<void> {
    let options = copy(data);
    options.forEach((shape: any) => {
      shape.color = AnnotationColorToHex(shape.color);
      if (shape.tool === 'oval' || shape.tool === 'rect') {
        shape.x = shape.x1;
        shape.y = shape.y1;
        shape.w = shape.x2;
        shape.h = shape.y2;
        shape.radius = 1; // setting radius to 1 to differentiate "old" and "new" shapes. In the past, we used to use teh tool name "rect" for circles... (they used radius: 0)
        shape.size = 0;
      }
    });
    const url = `${this.config.apiEndpoint}/assets/${asset.id}/annotations${this.getPassword(galleryId)}`;
    await lastValueFrom(this.http.post(url, { options }));
  }

  public fixAnnotation(comment: CommentDto) {
    if (!comment.annotation) return;
    comment.annotation.options.forEach(shape => {
      shape.color = AnnotationHexToColor(shape.color);

      if (shape['tool'] === <'rect'>'oval') {
        shape['x1'] = shape.x;
        shape['y1'] = shape.y;
        shape['x2'] = shape.w;
        shape['y2'] = shape.h;
      }

      if (shape.tool === 'rect' && shape.radius === 0) {
        shape.tool = <'rect'>'oval';
        shape['x1'] = shape.x;
        shape['y1'] = shape.y;
        shape['x2'] = shape.w;
        shape['y2'] = shape.h;
      }
    });
  }

  private getPassword(galleryId: string, and = false) {
    let password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    if (and) return '&password=' + encodeURIComponent(password);
    return '?password=' + encodeURIComponent(password);
  }
}
